/**
 * Classifies a `/license/refresh` reply. Only an explicit server denial may
 * block the device (and persist the offline lockdown); anything else is
 * transient and retried later, so a paying user is never locked out by a
 * 5xx, a rate limit or a malformed body.
 *
 *   - "ok":        2xx with `ok: true` and a token.
 *   - "denied":    HTTP 401/403, or a non-429 4xx with `ok: false` and a
 *                  string `reason` (e.g. 400 invalid_body).
 *   - "transient": everything else (5xx, 429, unexpected shapes).
 */
export type RefreshOutcome =
  | { kind: "ok"; token: string }
  | { kind: "denied"; reason: string }
  | { kind: "transient" };

export function classifyRefreshResponse(resp: unknown): RefreshOutcome {
  if (resp === null || typeof resp !== "object") return { kind: "transient" };
  const r = resp as { ok?: unknown; token?: unknown; reason?: unknown; httpStatus?: unknown };
  const status = typeof r.httpStatus === "number" ? r.httpStatus : 0;
  const reason = typeof r.reason === "string" && r.reason.length > 0 ? r.reason : null;

  if (status >= 200 && status < 300 && r.ok === true && typeof r.token === "string" && r.token) {
    return { kind: "ok", token: r.token };
  }
  if (status === 401 || status === 403) {
    return { kind: "denied", reason: reason ?? "license_invalid" };
  }
  if (status >= 400 && status < 500 && status !== 429 && r.ok === false && reason) {
    return { kind: "denied", reason };
  }
  return { kind: "transient" };
}
